import { Injectable } from '@angular/core';
import type { ApiInterface, PoiInterface } from '@navvis/ivion';
import { Vector3 } from 'three';

/** Location in local site coordinates as used by the middleware (SkomapLocation). */
export interface LocalLocation {
	lon: number;
	lat: number;
	z: number;
}

/**
 * Central IVION map navigation service.
 *
 * Single place for the "resolve POI id -> navigate -> highlight" flow that was
 * previously duplicated in search, FMS tree, favorites and app component
 * (legacy equivalent: IvService.navigateToPoiId).
 */
@Injectable({ providedIn: 'root' })
export class IvionNavigationService {

	/** IVION API instance exposed globally by src/index.ts. */
	private get ivApi(): ApiInterface | null {
		return (window as unknown as { IvionApp?: { ivApi?: ApiInterface } }).IvionApp?.ivApi ?? null;
	}

	/**
	 * Resolves a POI by id via the IVION repository.
	 * Returns null when IVION is not initialized or the POI does not exist.
	 */
	async findPoi(poiId: number): Promise<PoiInterface | null> {
		const ivApi = this.ivApi;
		if (!ivApi) {
			console.warn('[IvionNavigation] IVION API not available');
			return null;
		}

		try {
			return await ivApi.poi.repository.findOne(poiId) ?? null;
		} catch (err) {
			console.error('[IvionNavigation] Error resolving POI:', poiId, err);
			return null;
		}
	}

	/**
	 * Navigates the view to a POI id and highlights it (previous highlights are
	 * removed first). Returns the resolved POI, or null when navigation was not
	 * possible.
	 */
	async navigateToPoiId(poiId: number): Promise<PoiInterface | null> {
		const poi = await this.findPoi(poiId);
		if (!poi) {
			return null;
		}
		await this.navigateToPoi(poi);
		return poi;
	}

	/**
	 * Navigates the view to an already resolved POI and highlights it, the same
	 * way the legacy IndoorViewerMediator.navigateToPoiId did: previous
	 * highlights are removed, IVION's goToPoi moves the view, then the POI is
	 * highlighted.
	 *
	 * goToPoi moves the panorama to the closest image. When no image is
	 * reachable (the IVION user has no read access to the datasets around the
	 * POI, or the area is not scanned) IVION only moves the map, because
	 * src/index.ts disables allowPointCloudActivation like the legacy app did.
	 */
	async navigateToPoi(poi: PoiInterface): Promise<void> {
		const ivApi = this.ivApi;
		if (!ivApi) {
			console.warn('[IvionNavigation] IVION API not available');
			return;
		}

		try {
			// Unhighlight previously highlighted POIs (the SDK does not do it automatically)
			ivApi.poi.service.unhighlightPois(Array.from(ivApi.poi.service.highlightedPois ?? []));
			await ivApi.poi.service.goToPoi(poi);
			ivApi.poi.service.highlightPois([poi]);
		} catch (err) {
			console.error('[IvionNavigation] Error navigating to POI:', poi.id, err);
		}
	}

	/**
	 * Navigates the view to a location in local site coordinates when there is
	 * no POI to go to (e.g. a defect whose POI could not be resolved), the same
	 * way the legacy IndoorViewerMediator.navigateToLocation did: finds the
	 * closest image and moves the view to it.
	 * Returns false when no image is reachable from the location.
	 */
	async navigateToLocation(location: LocalLocation): Promise<boolean> {
		const ivApi = this.ivApi;
		if (!ivApi) {
			console.warn('[IvionNavigation] IVION API not available');
			return false;
		}

		try {
			const image = await ivApi.image.service.getClosestImage(new Vector3(location.lon, location.lat, location.z));
			// View direction and FOV are optional in IVION (left unchanged when
			// undefined) but declared as required in the typings.
			await ivApi.legacyApi.moveToImageId(image.id, undefined!, undefined!);
			return true;
		} catch (err) {
			console.warn('[IvionNavigation] No image reachable for location:', location, err);
			return false;
		}
	}
}
